// @flow
import React, { Component } from "react";
import ReactDOM from "react-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { browserHistory, Link } from "react-router";
import moment from "moment";

import type {
  UserEditStatesType,
  UserFormPropsType,
  EyeType,
  ResultStatesType,
} from "../types/UserFormType";
import Upload from "../component/Upload";
import Enrolments from "../component/Enrolments";
import {
  getUserInfo,
  enrolSingle,
  comparePrevPhoto,
} from "../../global/actions/databaseActions";
import {
  ErrosDisplay,
  setErrorMsg,
} from "../../global/components/ErrosDisplay";
import UserStepper from "../component/Stepper";
import CompleteMessage from "../../global/components/CompleteMessage";
import LoadingProgress from "../../global/components/LoadingProgress";
import { getCookie } from "../../global/functions/cookies";
import LogoffButton from "../../global/components/LogoffButton";
import { ColorSchema, GreyText } from "../../global/types/scss";
import { buttonStyles } from "../../global/components/StyledButtons";
import PhotoCheck from "../component/PhotoCheck";
import EnrolForm from "../component/EnrolForm";
import CompleteInstruction from "../component/CompleteInstruction";
import UserProfile from "../component/UserProfile";

import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Snackbar from "@material-ui/core/Snackbar";
import SnackbarContent from "@material-ui/core/SnackbarContent";
import Slide from "@material-ui/core/Slide";
import Button from "@material-ui/core/Button";
import { withStyles } from "@material-ui/core/styles";
import PersonIcon from "@material-ui/icons/Person";

function TransitionDown(props) {
  return <Slide direction="up" {...props} />;
}

class User extends Component<UserFormPropsType, UserEditStatesType> {
  constructor(props: UserFormPropsType) {
    super(props);
    this.state = {
      img: null,
      errors: {},
      completed: false,
      snackOpen: false,
      liveness: null,
    };
  }

  componentDidMount() {
    const accessToken = getCookie("access_token");
    if (accessToken === "") {
      this.setLoadError(
        "Access Code is not correct. Please check your Access Link."
      );
    } else {
      if (
        this.props.db.conference.id !== undefined &&
        this.props.db.user === undefined
      ) {
        this.props.actions.getUserInfo(
          this.props.db.conference.id,
          accessToken,
          "",
          () => null
        );
      }
    }
  }

  setLoadError = (error: string): * => {
    this.setErrorMessage("access_token", error);
  };

  setUpdateError = (error: string): * => {
    this.setErrorMessage("info_edit", error);
  };

  setImageError = (error: string): * => {
    this.setErrorMessage("image", error);
  };

  setLiveness = (json: *): * => {
    this.setState({ liveness: json });
  };

  setErrorMessage = (name: string, error: string): * => {
    if (error === "") {
      this.setState({ errors: setErrorMsg(this.state.errors, name, "", true) });
    } else {
      this.setState({ errors: setErrorMsg(this.state.errors, name, error) });
    }
  };

  setComplete = (): * => {
    this.setState({ completed: true });
  };

  setImg = (img: string): * => {
    this.setState({ img: img }, (): * => {
      if (this.enrol_form) {
        const domNode = ReactDOM.findDOMNode(this.enrol_form);
        domNode.scrollIntoView();
      }
    });
  };

  resetForm = (): * => {
    this.setImageError("");
    this.setState({ img: null });
  };

  formSubmitHandler = (eyes: EyeType): * => {
    if (this.state.img !== null && eyes !== null) {
      const result = {
        data: this.state.img,
        eyes: eyes,
        token: this.props.db.imgToken,
      };
      if (this.props.db.enrolments.length > 0) {
        this.comparePrevPhoto(result);
      } else {
        this.addEnrolment(result);
        this.resetForm();
      }
    }
  };

  addEnrolment = (result: ResultStatesType): * => {
    this.props.actions.enrolSingle(this.props.db.conference.id, {
      token: result.token,
      eyes: result.eyes,
    });
  };

  comparePrevPhoto = (result: ResultStatesType): * => {
    this.props.actions.comparePrevPhoto(
      this.props.db.conference.id,
      result.token,
      JSON.stringify(result.eyes),
      this.checkPhoto(result)
    );
  };

  checkPhoto = (result: ResultStatesType) => (match: boolean): * => {
    if (match) {
      this.addEnrolment(result);
      this.resetForm();
    } else {
      this.photoCheck.handleClickOpen(result);
    }
  };

  handleOnClick = (e: Event): * => {
    e.preventDefault();
    if (this.props.db.conference.active) {
      browserHistory.push("/" + this.props.params.conference + "/search");
    } else {
      this.setState({ snackOpen: true });
    }
  };

  handleClose = () => {
    this.setState({ snackOpen: false });
  };

  render(): React$Element<*> {
    const classes = this.props.classes;
    const user = this.props.db.user;
    const isActived =
      this.props.db.conference && this.props.db.conference.active;
    const isAdmin =
      user &&
      user.permissions &&
      user.permissions.indexOf("conference_admin") > -1;
    const requireMatched =
      this.props.db.enrolments && this.props.db.enrolments.length > 1;
    const isSearchForbid =
      !this.props.db.conference.allow_user_searching && !isAdmin;
    const allowMobile =
      this.props.db.conference && this.props.db.conference.allow_mobile_login;

    if (this.state.completed) {
      return (
        <CompleteMessage
          title="Thank you"
          content="Your facial recognition is completed.<br>
            The match score will be passed to the Identity Verification Service.<br>
            You can come back later and upload more photos.<br>
            The more photos your upload, the better your recognition becomes."
          buttonText={
            !isSearchForbid && isActived
              ? "Go to Selfie Upload for Verification"
              : "Back to My Profile"
          }
          onClick={
            !isSearchForbid && isActived
              ? () =>
                  browserHistory.push(
                    "/" + this.props.params.conference + "/search"
                  )
              : () => this.setState({ completed: false })
          }
          extraBlock={
            allowMobile && (
              <CompleteInstruction
                conferenceName={this.props.params.conference}
                accessToken={getCookie("access_token")}
                setLoadError={this.setLoadError}
              />
            )
          }
        />
      );
    } else {
      return (
        <div>
          <Typography
            variant="subheading"
            className="form-text hint-margin-bottom"
            style={{ color: ColorSchema("primary") }}
          >
            <PersonIcon
              className="medium-icon right-margin-5"
              style={{ verticalAlign: "bottom" }}
            />
            Your profile and photo(s).
          </Typography>
          <div className="top-banner">
            <ErrosDisplay errors={this.state.errors} form="user" />
          </div>
          {(user && (
            <Grid container spacing={8}>
              {(this.state.img && (
                <Grid item xs={12} ref={(re: *): * => (this.enrol_form = re)}>
                  <EnrolForm
                    img={this.state.img}
                    faces={this.props.db.detectedFaces}
                    cancelUpload={this.resetForm}
                    formSubmitHandler={this.formSubmitHandler}
                    setImageError={this.setImageError}
                    liveness={this.state.liveness}
                  />
                  <PhotoCheck
                    onRef={(ref: *): * => (this.photoCheck = ref)}
                    resetForm={this.resetForm}
                    addEnrolment={this.addEnrolment}
                  />
                </Grid>
              )) || (
                <Grid item xs={12} md={5}>
                  <Upload
                    setImageError={this.setImageError}
                    setImg={this.setImg}
                    setLiveness={this.setLiveness}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={7}>
                <UserProfile
                  conferenceFields={
                    this.props.db.conference.additional_person_fields
                  }
                  user={user}
                  setUpdateError={this.setUpdateError}
                />
              </Grid>
              <Grid item xs={12}>
                <UserStepper setComplete={this.setComplete} />
              </Grid>
              <Grid item xs={12}>
                <Enrolments token={this.props.params.token} />
              </Grid>
              {!isSearchForbid && (requireMatched || isAdmin) && (
                <Grid item xs={12} className="top-margin-30">
                  <Typography variant="body1" className="form-text">
                    <span
                      onClick={this.handleOnClick}
                      className="form-link-green"
                      style={
                        isActived
                          ? { color: ColorSchema("primary") }
                          : { color: GreyText }
                      }
                    >
                      Go to Selfie Upload for Verification
                    </span>
                  </Typography>
                  <Snackbar
                    open={this.state.snackOpen}
                    onClose={this.handleClose}
                    TransitionComponent={TransitionDown}
                    autoHideDuration={8000}
                    ContentProps={{
                      "aria-describedby": "message-id",
                    }}
                  >
                    <SnackbarContent
                      message={
                        <span id="message-id">
                          This feature will be available when the{" "}
                          {this.props.db.conference.display_name} conference
                          starts on{" "}
                          <span className="shine-title">
                            {moment
                              .utc(this.props.db.conference.start_date)
                              .local()
                              .format("DD/MMM/YYYY hh:mm A")}
                          </span>
                        </span>
                      }
                    />
                  </Snackbar>
                </Grid>
              )}
              {isAdmin && (
                <Grid item xs={12}>
                  <Link
                    to={"/" + this.props.params.conference + "/admin"}
                    className="form-link-green"
                    style={{ color: ColorSchema("primary") }}
                  >
                    Go to Admin Page
                  </Link>
                </Grid>
              )}
            </Grid>
          )) || (
            <div className="form-text">
              <LoadingProgress size={100} />
            </div>
          )}
          <LogoffButton />
        </div>
      );
    }
  }
}

function mapStateToProps(state) {
  return { db: state.forensic.db };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      { getUserInfo, enrolSingle, comparePrevPhoto },
      dispatch
    ),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(buttonStyles)(User));



// WEBPACK FOOTER //
// ./src/conference/views/User.js